export type ExportAuthenticatorAccount = {
  issuer: string;
  account: string;
  secret: string;
  digits: 6 | 8;
  algorithm: "SHA-1" | "SHA-256" | "SHA-512";
  period: number;
};

export type AuthenticatorQrImport = {
  accounts: ExportAuthenticatorAccount[];
  batchId: number;
  batchSize: number;
  batchIndex: number;
};

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const encoder = new TextEncoder();

function base32Bytes(value: string): Uint8Array {
  const clean = value.toUpperCase().replace(/[\s=-]/g, "");
  if (!clean || /[^A-Z2-7]/.test(clean)) throw new Error("Invalid Base32 secret");
  let bits = "";
  for (const char of clean) bits += alphabet.indexOf(char).toString(2).padStart(5, "0");
  const bytes: number[] = [];
  for (let index = 0; index + 8 <= bits.length; index += 8) bytes.push(Number.parseInt(bits.slice(index, index + 8), 2));
  return Uint8Array.from(bytes);
}

function bytesBase32(bytes: Uint8Array): string {
  let bits = "";
  for (const byte of bytes) bits += byte.toString(2).padStart(8, "0");
  let output = "";
  for (let index = 0; index < bits.length; index += 5) output += alphabet[Number.parseInt(bits.slice(index, index + 5).padEnd(5, "0"), 2)];
  return output;
}

function varint(value: number): number[] {
  const bytes: number[] = [];
  let remaining = Math.max(0, Math.floor(value));
  do {
    let byte = remaining % 128;
    remaining = Math.floor(remaining / 128);
    if (remaining > 0) byte |= 0x80;
    bytes.push(byte);
  } while (remaining > 0);
  return bytes;
}

function bytesField(field: number, value: Uint8Array): number[] {
  return [...varint(field << 3 | 2), ...varint(value.length), ...value];
}

function stringField(field: number, value: string): number[] {
  return value ? bytesField(field, encoder.encode(value)) : [];
}

function numberField(field: number, value: number): number[] {
  return [...varint(field << 3), ...varint(value)];
}

function otpParameters(account: ExportAuthenticatorAccount): Uint8Array {
  const algorithm = account.algorithm === "SHA-256" ? 2 : account.algorithm === "SHA-512" ? 3 : 1;
  const digits = account.digits === 8 ? 2 : 1;
  return Uint8Array.from([
    ...bytesField(1, base32Bytes(account.secret)),
    ...stringField(2, account.account),
    ...stringField(3, account.issuer),
    ...numberField(4, algorithm),
    ...numberField(5, digits),
    ...numberField(6, 2)
  ]);
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
}

export function googleAuthenticatorMigrationUris(
  accounts: ExportAuthenticatorAccount[],
  options: { maxPerQr?: number; batchId?: number } = {}
): string[] {
  if (!accounts.length) throw new Error("Select at least one account");
  const unsupported = accounts.find((account) => account.period !== 30);
  if (unsupported) throw new Error(`${unsupported.issuer || unsupported.account} uses a non-standard period and cannot be transferred to Google Authenticator.`);
  const maxPerQr = Math.max(1, Math.min(10, options.maxPerQr ?? 7));
  const groups: ExportAuthenticatorAccount[][] = [];
  for (let index = 0; index < accounts.length; index += maxPerQr) groups.push(accounts.slice(index, index + maxPerQr));
  const batchId = options.batchId ?? crypto.getRandomValues(new Uint32Array(1))[0]! & 0x7fffffff;
  return groups.map((group, batchIndex) => {
    const payload = Uint8Array.from([
      ...group.flatMap((account) => bytesField(1, otpParameters(account))),
      ...numberField(2, 1),
      ...numberField(3, groups.length),
      ...numberField(4, batchIndex),
      ...numberField(5, batchId)
    ]);
    return `otpauth-migration://offline?data=${encodeURIComponent(toBase64(payload))}`;
  });
}

function readVarint(bytes: Uint8Array, start: number): [number, number] {
  let value = 0;
  let multiplier = 1;
  let offset = start;
  while (offset < bytes.length) {
    const byte = bytes[offset++]!;
    value += (byte & 0x7f) * multiplier;
    if ((byte & 0x80) === 0) return [value, offset];
    multiplier *= 128;
    if (multiplier > Number.MAX_SAFE_INTEGER) break;
  }
  throw new Error("Invalid Google Authenticator transfer data");
}

function readLengthDelimited(bytes: Uint8Array, start: number): [Uint8Array, number] {
  const [length, offset] = readVarint(bytes, start);
  const end = offset + length;
  if (end > bytes.length) throw new Error("Truncated Google Authenticator transfer data");
  return [bytes.slice(offset, end), end];
}

function skipValue(bytes: Uint8Array, offset: number, wireType: number): number {
  if (wireType === 0) return readVarint(bytes, offset)[1];
  if (wireType === 1) return offset + 8;
  if (wireType === 2) return readLengthDelimited(bytes, offset)[1];
  if (wireType === 5) return offset + 4;
  throw new Error("Unsupported Google Authenticator transfer field");
}

function parseOtpParameters(bytes: Uint8Array): ExportAuthenticatorAccount | null {
  let offset = 0;
  let secret = new Uint8Array();
  let name = "";
  let issuer = "";
  let algorithm = 1;
  let digits = 1;
  let type = 2;
  while (offset < bytes.length) {
    const [tag, next] = readVarint(bytes, offset);
    offset = next;
    const field = tag >> 3;
    const wireType = tag & 7;
    if ((field === 1 || field === 2 || field === 3) && wireType === 2) {
      const [value, end] = readLengthDelimited(bytes, offset);
      offset = end;
      if (field === 1) secret = Uint8Array.from(value);
      else if (field === 2) name = new TextDecoder().decode(value);
      else issuer = new TextDecoder().decode(value);
    } else if ((field === 4 || field === 5 || field === 6) && wireType === 0) {
      const [value, end] = readVarint(bytes, offset);
      offset = end;
      if (field === 4) algorithm = value;
      else if (field === 5) digits = value;
      else type = value;
    } else offset = skipValue(bytes, offset, wireType);
  }
  if (type !== 2) return null;
  if (!secret.length) throw new Error("An imported account is missing its setup key");
  const algorithms = { 0: "SHA-1", 1: "SHA-1", 2: "SHA-256", 3: "SHA-512" } as const;
  const algorithmName = algorithms[algorithm as keyof typeof algorithms];
  if (!algorithmName) throw new Error("An imported account uses an unsupported algorithm");
  if (!issuer && name.includes(":")) {
    const separator = name.indexOf(":");
    issuer = name.slice(0, separator).trim();
    name = name.slice(separator + 1).trim();
  } else if (issuer && name.toLocaleLowerCase().startsWith(`${issuer.toLocaleLowerCase()}:`)) name = name.slice(issuer.length + 1).trim();
  return {
    issuer: issuer.trim() || "Imported account",
    account: name.trim(),
    secret: bytesBase32(secret),
    digits: digits === 2 ? 8 : 6,
    period: 30,
    algorithm: algorithmName
  };
}

function migrationBytes(uri: URL): Uint8Array {
  const encoded = uri.searchParams.get("data");
  if (!encoded) throw new Error("Google Authenticator transfer QR has no data");
  const base64 = encoded.replace(/ /g, "+").replace(/-/g, "+").replace(/_/g, "/");
  try { return Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")), (character) => character.charCodeAt(0)); }
  catch { throw new Error("Google Authenticator transfer QR is damaged"); }
}

export function parseAuthenticatorQr(value: string): AuthenticatorQrImport {
  const raw = value.trim();
  if (raw.toLocaleLowerCase().startsWith("otpauth://")) {
    const uri = new URL(raw);
    const label = decodeURIComponent(uri.pathname.replace(/^\//, ""));
    const separator = label.indexOf(":");
    const issuer = (uri.searchParams.get("issuer") ?? (separator >= 0 ? label.slice(0, separator) : "Imported account")).trim();
    const account = (separator >= 0 ? label.slice(separator + 1) : label).trim();
    const secret = (uri.searchParams.get("secret") ?? "").trim().toUpperCase().replace(/[\s=-]/g, "");
    base32Bytes(secret);
    const digitsValue = Number(uri.searchParams.get("digits") ?? 6);
    const period = Number(uri.searchParams.get("period") ?? 30);
    const algorithmValue = (uri.searchParams.get("algorithm") ?? "SHA1").toUpperCase().replace(/-/g, "");
    const algorithms = { SHA1: "SHA-1", SHA256: "SHA-256", SHA512: "SHA-512" } as const;
    const algorithm = algorithms[algorithmValue as keyof typeof algorithms];
    if ((digitsValue !== 6 && digitsValue !== 8) || !Number.isInteger(period) || period < 1 || period > 300 || !algorithm) throw new Error("Unsupported authenticator QR settings");
    return { accounts: [{ issuer: issuer || "Imported account", account, secret, digits: digitsValue, period, algorithm }], batchId: 0, batchSize: 1, batchIndex: 0 };
  }
  const uri = new URL(raw);
  if (uri.protocol !== "otpauth-migration:") throw new Error("This QR code is not an authenticator setup or transfer code");
  const bytes = migrationBytes(uri);
  const accounts: ExportAuthenticatorAccount[] = [];
  let batchId = 0;
  let batchSize = 1;
  let batchIndex = 0;
  let offset = 0;
  while (offset < bytes.length) {
    const [tag, next] = readVarint(bytes, offset);
    offset = next;
    const field = tag >> 3;
    const wireType = tag & 7;
    if (field === 1 && wireType === 2) {
      const [value, end] = readLengthDelimited(bytes, offset);
      offset = end;
      const account = parseOtpParameters(value);
      if (account) accounts.push(account);
    } else if ((field === 3 || field === 4 || field === 5) && wireType === 0) {
      const [value, end] = readVarint(bytes, offset);
      offset = end;
      if (field === 3) batchSize = value;
      else if (field === 4) batchIndex = value;
      else batchId = value;
    } else offset = skipValue(bytes, offset, wireType);
  }
  if (!accounts.length) throw new Error("No TOTP accounts were found in this transfer QR");
  if (batchSize < 1 || batchIndex < 0 || batchIndex >= batchSize) throw new Error("Invalid Google Authenticator transfer batch");
  return { accounts, batchId, batchSize, batchIndex };
}
